import React, { useEffect, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Creators as ProductCreators } from 'store/products/products-duck.js';
import { Box, Button, Typography, Paper, IconButton } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ProductModal from './modal';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation.js';
import DeleteConfirmationModal from 'components/delete-confirmation-modal/index.jsx';
import PropTypes from 'prop-types';
import { DataGrid } from '@mui/x-data-grid';
import ProductFilters from './filters/index.jsx';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const ProductsManagementPage = ({
  products,
  filters,
  productsSetFilters,
  productsFetchAsync,
  productOpenModal,
  productFetchByIdAsync,
  productDeleteAsync,
  totalCount,
}) => {
  const {
    isOpen,
    message,
    open: openDeleteModal,
    close: closeDeleteModal,
    confirm: confirmDelete,
  } = useDeleteConfirmation();
  const [paginationModel, setPaginationModel] = useState({
    page: filters.page ? filters.page - 1 : 0,
    pageSize: 10,
  });

  const fetchProducts = useCallback(() => {
    productsFetchAsync({
      ...filters,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    });
  }, [productsFetchAsync, paginationModel, filters]);

  const handlePaginationChange = (model) => {
    setPaginationModel(model);
    productsSetFilters({ ...filters, page: model.page + 1 });
    productsFetchAsync({ ...filters, page: model.page + 1, pageSize: model.pageSize });
  };

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const columns = [
    { field: 'name', headerName: 'Nome', flex: 1 },
    { field: 'price', headerName: 'Preço', flex: 1 },
    { field: 'promotionalPrice', headerName: 'Preço Promocional', flex: 1 },
    { field: 'category', headerName: 'Categoria', flex: 1 },
    { field: 'subCategory', headerName: 'Subcategoria', flex: 1 },
    {
      field: 'active',
      headerName: 'Ativo',
      flex: 1,
      renderCell: (params) => (params.row.active ? <CheckIcon color="success" /> : <BlockIcon />),
    },
    {
      field: 'highlighted',
      headerName: 'Destaque',
      flex: 1,
      renderCell: (params) =>
        params.row.highlighted ? <StarIcon color="warning" /> : <StarBorderIcon />,
    },
    {
      field: 'actions',
      headerName: 'Ações',
      flex: 1,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => productFetchByIdAsync(params.row.id)}>
            <VisibilityIcon />
          </IconButton>
          <IconButton onClick={() => productFetchByIdAsync(params.row.id)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() =>
              openDeleteModal(
                () => productDeleteAsync(params.row.id),
                'Deseja remover este produto?',
              )
            }
            color="error"
          >
            <DeleteIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              productFetchByIdAsync(params.row.id, true);
            }}
          >
            <ContentCopyIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <ProductModal />
      <DeleteConfirmationModal
        isOpen={isOpen}
        message={message}
        onConfirm={confirmDelete}
        onClose={closeDeleteModal}
      />
      <Typography variant="h5" gutterBottom>
        Gestão de Produtos
      </Typography>
      <ProductFilters />
      <Button
        color="primary"
        variant="contained"
        onClick={() =>
          productOpenModal({
            id: null,
            name: '',
            price: '',
            promotionalPrice: '',
            description: '',
            technicalSpecifications: '',
          })
        }
        sx={{ mb: 2 }}
      >
        Adicionar Produto
      </Button>
      <Paper sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={products}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          pageSizeOptions={[10]}
          sx={{ border: 0, height: '70vh' }}
        />
      </Paper>
    </>
  );
};

const mapStateToProps = (state) => ({
  totalCount: state.Product.totalCount,
  products: state.Product.products,
  filters: state.Product.filters,
});

const mapDispatchToProps = (dispatch) => bindActionCreators(ProductCreators, dispatch);

ProductsManagementPage.propTypes = {
  totalCount: PropTypes.number.isRequired,
  products: PropTypes.array.isRequired,
  filters: PropTypes.object.isRequired,
  productsFetchAsync: PropTypes.func.isRequired,
  productOpenModal: PropTypes.func.isRequired,
  productFetchByIdAsync: PropTypes.func.isRequired,
  productDeleteAsync: PropTypes.func.isRequired,
  productsSetFilters: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductsManagementPage);
